import { useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { type ExternalAccountDetailsAttributes } from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { useGetPayoutSettings } from "@src/appV2/redesign/Accounts/PayoutSettings/api/useGetPayoutSettings";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { CancelShiftBottomSheet } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/components/CancelShiftBottomSheet";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { isPast, parseISO } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { PageContent } from "../../WorkerShifts/components/PageContent";
import { type ShiftPaymentStatus, useGetShiftPaymentStatus } from "../api/useGetShiftPaymentStatus";
import { useShiftDetails } from "../api/useShiftDetails";
import { useShiftScoreLedger } from "../api/useShiftScoreLedger";
import { GUIDED_SUPPORT_FULL_PATH } from "../paths";
import { type ShiftDetailsRouteParams } from "../types";
import { CancellationPolicyInfoBottomSheet } from "./components/CancellationPolicyInfoBottomSheet";
import { ErrorState } from "./components/ErrorState";
import { LoadingState } from "./components/LoadingState";
import { ShiftDetailsContent } from "./components/ShiftDetailsContent";
import { SupportAttendanceConfirmationBottomSheet } from "./components/SupportAttendanceConfirmationBottomSheet";
import { useSupportCancelShiftFlow } from "./hooks/useSupportCancelShiftFlow";

export function ShiftDetailsPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { shiftId } = useParams<ShiftDetailsRouteParams>();
  const worker = useDefinedWorker();

  const {
    data: shiftDetails,
    isLoading: isShiftLoading,
    isError: isShiftError,
    refetch: refetchShift,
  } = useShiftDetails({ shiftId });

  const isPastShift = isDefined(shiftDetails?.startAt) && isPast(parseISO(shiftDetails.startAt));

  // Attendance confirmation state for upcoming shifts
  const attendanceModalState = useModalState();
  const policyInfoModalState = useModalState();
  const [hasRespondedToAttendance, setHasRespondedToAttendance] = useState(false);

  const shiftStartDate = isDefined(shiftDetails?.startAt)
    ? parseISO(shiftDetails.startAt)
    : undefined;

  const { startCancelFlow, cancelShiftBottomSheetProps } = useSupportCancelShiftFlow({
    shiftId,
    shiftStartDate,
  });

  // Log page view when shift details load
  useEffect(() => {
    if (isDefined(shiftDetails)) {
      logEvent(APP_V2_USER_EVENTS.GUIDED_SUPPORT_SHIFT_DETAILS_VIEWED, {
        shiftId,
        isPastShift,
      });
    }
  }, [shiftId, shiftDetails, isPastShift]);

  // Auto-open attendance confirmation for upcoming shifts
  useEffect(() => {
    if (isDefined(shiftDetails) && !isPastShift && !hasRespondedToAttendance) {
      attendanceModalState.openModal();
    }
  }, [shiftDetails, isPastShift, hasRespondedToAttendance, attendanceModalState]);

  const { data: scoreLedger = [], isLoading: isScoreLedgerLoading } = useShiftScoreLedger(
    { workerId: worker.userId, shiftId },
    { enabled: isPastShift }
  );

  const { data: payoutSettings } = useGetPayoutSettings({
    includeExternalAccount: isPastShift,
  });
  const payoutDestination = payoutSettings?.destinationDetails;

  // Fetch payment status from the same API as the Earnings page
  // This ensures status consistency between pages
  const { data: paymentStatus, isLoading: isPaymentStatusLoading } = useGetShiftPaymentStatus({
    shiftId,
    enabled: isPastShift,
  });

  const handleConfirmAttendance = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.GUIDED_SUPPORT_ATTENDANCE_CONFIRMED, { shiftId });
    attendanceModalState.closeModal();
    setHasRespondedToAttendance(true);
  }, [attendanceModalState, shiftId]);

  const handleDeclineAttendance = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.GUIDED_SUPPORT_ATTENDANCE_DECLINED, { shiftId });
    setHasRespondedToAttendance(true);
    attendanceModalState.closeModal();
    policyInfoModalState.openModal();
  }, [attendanceModalState, policyInfoModalState, shiftId]);

  const handleContinueToCancel = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.GUIDED_SUPPORT_CONTINUE_TO_CANCEL_TAPPED, { shiftId });
    policyInfoModalState.closeModal();
    startCancelFlow();
  }, [policyInfoModalState, startCancelFlow, shiftId]);

  const handlePolicyInfoGoBack = useCallback(() => {
    policyInfoModalState.closeModal();
  }, [policyInfoModalState]);

  const { showErrorToast } = useToast();

  const handleReportIssue = useCallback(async () => {
    logEvent(APP_V2_USER_EVENTS.GUIDED_SUPPORT_REPORT_ISSUE_TAPPED, { shiftId });
    try {
      await openZendeskMessaging();
    } catch {
      showErrorToast("Unable to open support chat. Please try again.");
    }
  }, [shiftId, showErrorToast]);

  const handleRetry = useCallback(() => {
    void refetchShift();
  }, [refetchShift]);

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          fallbackGoBackPath={GUIDED_SUPPORT_FULL_PATH}
          headerVariant="tertiary"
          scrollContainerRef={scrollRef}
          title="Shift details"
        />
        <PageContent
          ref={scrollRef}
          sx={(theme) => ({
            flex: 1,
            padding: theme.spacing(4),
          })}
        >
          <ShiftDetailsPageContent
            handleRetry={handleRetry}
            isPaymentStatusLoading={isPaymentStatusLoading}
            isScoreLedgerLoading={isScoreLedgerLoading}
            isShiftError={isShiftError}
            isShiftLoading={isShiftLoading}
            isPastShift={isPastShift}
            paymentStatus={paymentStatus}
            payoutDestination={payoutDestination}
            scoreLedger={scoreLedger}
            shiftDetails={shiftDetails}
          />
        </PageContent>

        <PageFooterWrapper>
          <LoadingButton
            fullWidth
            isLoading={false}
            size="large"
            variant="contained"
            onClick={handleReportIssue}
          >
            Report an Issue
          </LoadingButton>
        </PageFooterWrapper>
      </PageWrapper>

      {!isPastShift && isDefined(shiftDetails) && !policyInfoModalState.modalIsOpen && (
        <SupportAttendanceConfirmationBottomSheet
          modalState={attendanceModalState}
          shiftDetails={shiftDetails}
          onConfirmAttendance={handleConfirmAttendance}
          onDeclineAttendance={handleDeclineAttendance}
        />
      )}

      {!isPastShift && isDefined(shiftDetails) && !attendanceModalState.modalIsOpen && (
        <CancellationPolicyInfoBottomSheet
          modalState={policyInfoModalState}
          shiftId={shiftId}
          onContinueToCancel={handleContinueToCancel}
          onGoBack={handlePolicyInfoGoBack}
        />
      )}

      {isDefined(cancelShiftBottomSheetProps) && (
        <CancelShiftBottomSheet {...cancelShiftBottomSheetProps} />
      )}
    </ThemeProvider>
  );
}

interface ShiftDetailsPageContentProps {
  isShiftLoading: boolean;
  isShiftError: boolean;
  shiftDetails: ReturnType<typeof useShiftDetails>["data"];
  isPastShift: boolean;
  isScoreLedgerLoading: boolean;
  scoreLedger: ReturnType<typeof useShiftScoreLedger>["data"];
  payoutDestination: ExternalAccountDetailsAttributes | undefined;
  paymentStatus: ShiftPaymentStatus | undefined;
  isPaymentStatusLoading: boolean;
  handleRetry: () => void;
}

function ShiftDetailsPageContent(props: ShiftDetailsPageContentProps) {
  const {
    isShiftLoading,
    isShiftError,
    shiftDetails,
    isPastShift,
    isScoreLedgerLoading,
    scoreLedger,
    payoutDestination,
    paymentStatus,
    isPaymentStatusLoading,
    handleRetry,
  } = props;

  if (isShiftLoading) {
    return <LoadingState />;
  }

  if (isShiftError || !isDefined(shiftDetails)) {
    return <ErrorState onRetry={handleRetry} />;
  }

  return (
    <ShiftDetailsContent
      isPastShift={isPastShift}
      isPaymentStatusLoading={isPaymentStatusLoading}
      isScoreLedgerLoading={isScoreLedgerLoading}
      paymentStatus={paymentStatus}
      payoutDestination={payoutDestination}
      scoreLedger={scoreLedger ?? []}
      shiftDetails={shiftDetails}
    />
  );
}
